/**
 * HR Shifts — Phase 3.
 *   Tab 1: Shift master CRUD (times + night premium config)
 *   Tab 2: Swap requests queue (approve / reject)
 *
 * Per-employee recurring assignment is edited from the employee's row
 * via a small inline picker; daily roster overrides + the swap flow
 * cover the rest.
 */
import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import {
  useShiftMaster,
  useUpsertShiftMaster,
  useDeleteShiftMaster,
  useSwapRequests,
  useDecideSwap,
  useEmployees,
  useWorkSchedule,
  useHolidays,
  useShiftAssignments,
  useRoster,
  useApprovedLeaves,
  useRecomputeCompHolidays,
  useRotationPatterns,
  useUpsertRotationPattern,
  useDeleteRotationPattern,
  useGenerateRotation,
  type ShiftMasterRow,
  type ShiftAssignmentRow,
  type RosterRow,
  type RotationPatternRow,
} from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import {
  useShiftAssignmentsByShift,
  useUpsertShiftAssignment,
  useDeleteShiftAssignment,
} from '../../hooks';
import { Plus, Pencil, Trash2, X, Save, Check, XCircle, Moon, ChevronLeft, ChevronRight, RotateCw, Users } from 'lucide-react';
import { NIGHT_PREMIUM_TYPES } from '@payroll/shared';
import { EmployeePicker, EmployeeMultiPicker } from '../../components/ui/EmployeePicker';

// ISO weekday order for the chips, Thai labels.
const WEEKDAYS: Array<{ iso: number; label: string }> = [
  { iso: 1, label: 'จ' }, { iso: 2, label: 'อ' }, { iso: 3, label: 'พ' },
  { iso: 4, label: 'พฤ' }, { iso: 5, label: 'ศ' }, { iso: 6, label: 'ส' }, { iso: 7, label: 'อา' },
];
const WD_FULL: Record<number, string> = { 1: 'จันทร์', 2: 'อังคาร', 3: 'พุธ', 4: 'พฤหัส', 5: 'ศุกร์', 6: 'เสาร์', 7: 'อาทิตย์' };

function firstOfMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

const EMPTY: Partial<ShiftMasterRow> = {
  code: '',
  name: '',
  start_time: '08:00',
  end_time: '17:00',
  crosses_midnight: false,
  break_minutes: 60,
  paid_hours: 8,
  night_premium_type: 'none',
  night_premium_value: 0,
  night_window_start: '22:00',
  night_window_end: '06:00',
  default_weekdays: [1, 2, 3, 4, 5, 6],
  active: true,
  flexible: false,
  require_clock_out: true,
  flex_check_in_by: null,
};

// ตัวอย่างกดใช้: กะยืดหยุ่น เข้าก่อนเที่ยง สแกนเข้าอย่างเดียว
const FLEX_PRESET: Partial<ShiftMasterRow> = {
  code: 'FLEX',
  name: 'กะยืดหยุ่น (เข้าก่อนเที่ยง สแกนเข้าอย่างเดียว)',
  start_time: '12:00',
  end_time: '21:00',
  crosses_midnight: false,
  break_minutes: 60,
  paid_hours: 8,
  night_premium_type: 'none',
  night_premium_value: 0,
  night_window_start: '22:00',
  night_window_end: '06:00',
  default_weekdays: [1, 2, 3, 4, 5],
  active: true,
  flexible: true,
  require_clock_out: false,
  flex_check_in_by: '12:00',
};

const TAB_LABEL = { calendar: 'ปฏิทิน', master: 'นิยามกะ', rotation: 'หมุนเวร', swap: 'คำขอแลกเวร' } as const;

/** Content-only version used by the Time hub. */
export function HRShiftsContent() {
  const [tab, setTab] = useState<'calendar' | 'master' | 'rotation' | 'swap'>('calendar');
  return (
    <>
      <div className="flex gap-1 mb-3 border-b border-gray-200">
        {(['calendar', 'master', 'rotation', 'swap'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm font-medium border-b-2 ${
              tab === t ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500'
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </div>
      {tab === 'calendar' ? <CalendarTab /> : tab === 'master' ? <MasterTab /> : tab === 'rotation' ? <RotationTab /> : <SwapTab />}
    </>
  );
}

/** Standalone page kept for legacy /hr/shifts (now redirects via Time hub). */
export function HRShifts() {
  return (
    <AdminLayout title="กะการทำงาน">
      <HRShiftsContent />
    </AdminLayout>
  );
}

// === Calendar tab — per-employee month view with holidays + comp days ===
const SHIFT_COLORS = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700', 'bg-cyan-100 text-cyan-700',
];
const DOW_HEADERS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];
const LEAVE_TYPE_LABEL: Record<string, string> = { sick: 'ลาป่วย', personal: 'ลากิจ', vacation: 'ลาพักร้อน', maternity: 'ลาคลอด', ordination: 'ลาบวช' };
const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

type EmpLite = { emp_code: string; first_name: string; last_name: string; nickname?: string | null; department?: string | null; position?: string | null };
type HolidayLite = { date: string; name: string; auto_generated?: boolean };

const pad = (n: number) => String(n).padStart(2, '0');
/** ISO weekday 1=Mon..7=Sun for a Y/M(1-12)/D, computed in UTC. */
function isoDowOf(y: number, m: number, d: number): number {
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun
  return dow === 0 ? 7 : dow;
}

function CalendarTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [empCode, setEmpCode] = useState<string | null>(null);

  const { data: employees } = useEmployees() as { data: EmpLite[] | undefined };
  const { data: schedule } = useWorkSchedule();
  const { data: holidays } = useHolidays(year) as { data: HolidayLite[] | undefined };
  const { data: shifts } = useShiftMaster();
  const { data: assignments } = useShiftAssignments(empCode);
  const recompute = useRecomputeCompHolidays();

  const from = `${year}-${pad(month)}-01`;
  const to = `${year}-${pad(month)}-${pad(new Date(year, month, 0).getDate())}`;
  const { data: roster } = useRoster(from, to);
  const { data: approvedLeaves } = useApprovedLeaves(from, to);

  // วันลาอนุมัติแล้วของพนักงานที่เลือก → โชว์ป้าย "ลา" กันลงเวรทับ
  const leaveDates = new Map<string, string>();
  for (const l of approvedLeaves ?? []) if (l.emp_code === empCode) leaveDates.set(l.date, l.leave_type);

  const weekend = new Set<number>(schedule?.weekend_days ?? [7]);
  const shiftById = new Map((shifts ?? []).map((s, i) => [s.id, { row: s, color: SHIFT_COLORS[i % SHIFT_COLORS.length] }]));
  const holidayByDate = new Map<string, HolidayLite>();
  for (const h of holidays ?? []) if (!holidayByDate.has(h.date)) holidayByDate.set(h.date, h);
  const rosterByDate = new Map<string, RosterRow>();
  for (const r of roster ?? []) if (r.emp_code === empCode) rosterByDate.set(r.date, r);

  // Resolve the assignment-pattern shift for a date (recurring weekdays).
  function assignedShift(dateIso: string, isoDow: number): string | null {
    for (const a of (assignments ?? []) as ShiftAssignmentRow[]) {
      if (!a.active) continue;
      if (a.start_date > dateIso) continue;
      if (a.end_date && a.end_date < dateIso) continue;
      if (a.weekdays?.includes(isoDow)) return a.shift_id;
    }
    return null;
  }

  const daysInMonth = new Date(year, month, 0).getDate();
  const leadBlanks = new Date(Date.UTC(year, month - 1, 1)).getUTCDay(); // 0=Sun → Sun-first grid
  const cells: Array<number | null> = [...Array(leadBlanks).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  function prevMonth() { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); }
  function nextMonth() { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <EmployeePicker
          employees={employees ?? []}
          value={empCode}
          onChange={setEmpCode}
          placeholder="— เลือกพนักงาน (ดูเฉพาะวันหยุด) —"
          className="flex-1 min-w-[200px]"
        />
        <div className="flex items-center gap-1">
          <button onClick={prevMonth} className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
          <div className="text-sm font-medium w-28 text-center">{THAI_MONTHS[month - 1]} {year + 543}</div>
          <button onClick={nextMonth} className="p-1.5 rounded-lg border border-gray-300 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-medium text-gray-400">
        {DOW_HEADERS.map((d, i) => <div key={d} className={i === 0 || i === 6 ? 'text-red-400' : ''}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((day, idx) => {
          if (day === null) return <div key={`b${idx}`} />;
          const dateIso = `${year}-${pad(month)}-${pad(day)}`;
          const isoDow = isoDowOf(year, month, day);
          const holiday = holidayByDate.get(dateIso);
          const isWeekend = weekend.has(isoDow);
          const rosterRow = rosterByDate.get(dateIso);
          // Shift resolution: explicit roster (incl. null = off) wins, else assignment pattern.
          let shiftId: string | null = null;
          let explicitOff = false;
          if (rosterRow) { shiftId = rosterRow.shift_id; explicitOff = rosterRow.shift_id === null; }
          else if (empCode) shiftId = assignedShift(dateIso, isoDow);
          const shift = shiftId ? shiftById.get(shiftId) : null;
          const leaveType = empCode ? leaveDates.get(dateIso) : undefined;

          const bg = holiday
            ? (holiday.auto_generated ? 'bg-amber-50' : 'bg-red-50')
            : isWeekend ? 'bg-gray-50' : 'bg-white';
          return (
            <div key={dateIso} className={`min-h-[68px] rounded-lg border border-gray-100 p-1 ${bg} flex flex-col gap-0.5`}>
              <div className={`text-xs font-medium ${holiday ? (holiday.auto_generated ? 'text-amber-700' : 'text-red-600') : isWeekend ? 'text-gray-400' : 'text-gray-600'}`}>{day}</div>
              {holiday && <div className={`text-[9px] leading-tight ${holiday.auto_generated ? 'text-amber-600' : 'text-red-500'} line-clamp-2`}>{holiday.name}</div>}
              {leaveType && !holiday && (
                <div className={`text-[9px] leading-tight rounded px-1 py-0.5 bg-rose-100 text-rose-700 truncate ${shift ? 'ring-1 ring-red-400' : ''}`} title="ลาอนุมัติแล้ว — ลงเวรทำงานทับไม่ได้">
                  {LEAVE_TYPE_LABEL[leaveType] ?? 'ลา'}{shift ? ' ⚠️' : ''}
                </div>
              )}
              {empCode && !holiday && !leaveType && shift && (
                <div className={`text-[9px] leading-tight rounded px-1 py-0.5 ${shift.color} truncate`}>{shift.row.name}</div>
              )}
              {empCode && !holiday && !leaveType && !shift && (explicitOff || isWeekend) && (
                <div className="text-[9px] text-gray-400">หยุด</div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2 pt-1">
        <div className="flex items-center gap-3 text-[11px] text-gray-500 flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200" /> วันหยุด</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200" /> ชดเชย</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-gray-50 border border-gray-200" /> หยุดประจำสัปดาห์</span>
        </div>
        <button
          onClick={async () => { await recompute.mutateAsync(year); }}
          disabled={recompute.isPending}
          className="btn-secondary btn-slim"
          title="คำนวณวันหยุดชดเชยใหม่ตามวันหยุดบริษัท + วันหยุดประจำสัปดาห์"
        >
          <RotateCw className="w-3.5 h-3.5" /> {recompute.isPending ? 'กำลังคำนวณ…' : 'คำนวณวันหยุดชดเชย'}
        </button>
      </div>
      {!empCode && <p className="text-[11px] text-gray-400">เลือกพนักงานเพื่อดูตารางกะรายคน · วันหยุด/ชดเชยแสดงกับทุกคน</p>}
    </div>
  );
}

function MasterTab() {
  const { data, isLoading } = useShiftMaster();
  const { data: allAssigns } = useShiftAssignments(null);
  const upsert = useUpsertShiftMaster();
  const remove = useDeleteShiftMaster();
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Partial<ShiftMasterRow> | null>(null);
  const [assigning, setAssigning] = useState<ShiftMasterRow | null>(null);

  // Active employees per shift.
  const countByShift = new Map<string, Set<string>>();
  for (const a of (allAssigns ?? []) as ShiftAssignmentRow[]) {
    if (!a.active) continue;
    if (!countByShift.has(a.shift_id)) countByShift.set(a.shift_id, new Set());
    countByShift.get(a.shift_id)!.add(a.emp_code);
  }

  async function handleSave() {
    if (!editing?.code || !editing.name) { toast.error('กรอก code + ชื่อ'); return; }
    try {
      await upsert.mutateAsync(editing);
      setEditing(null);
    } catch (e) { toast.error(`บันทึกไม่สำเร็จ: ${e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'}`); }
  }

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-3">
        <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary text-sm">
          <Plus className="w-4 h-4" /> เพิ่มกะ
        </button>
        <button onClick={() => setEditing({ ...FLEX_PRESET })} className="btn-secondary text-sm" title="เติมตัวอย่างกะยืดหยุ่นให้ในฟอร์ม — ปรับแล้วกดบันทึก">
          ⏱️ ตัวอย่าง: กะยืดหยุ่น
        </button>
      </div>

      {isLoading ? (
        <p className="text-center text-gray-500 py-8">กำลังโหลด…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {(data ?? []).map((s) => (
            <div key={s.id} className="card p-3">
              <div className="flex justify-between items-start gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {s.name} <span className="text-xs text-gray-400 font-mono">{s.code}</span>
                    <span className="badge bg-brand-100 text-brand-700 text-[10px] inline-flex items-center gap-0.5"><Users className="w-3 h-3" />{(countByShift.get(s.id)?.size ?? 0)} คน</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {s.start_time}–{s.end_time}{s.crosses_midnight && ' (ข้ามวัน)'} · พัก {s.break_minutes} น. · {s.paid_hours} ชม.
                  </div>
                  {s.flexible && (
                    <div className="text-[11px] text-teal-700 mt-1">
                      ⏱️ ยืดหยุ่น · ไม่คิดสาย{s.require_clock_out === false ? ' · สแกนเข้าอย่างเดียว' : ''}{s.flex_check_in_by ? ` · เข้าก่อน ${s.flex_check_in_by}` : ''}
                    </div>
                  )}
                  <div className="flex gap-1 mt-1">
                    {WEEKDAYS.map((w) => {
                      const on = (s.default_weekdays ?? []).includes(w.iso);
                      return <span key={w.iso} className={`text-[10px] w-5 h-5 inline-flex items-center justify-center rounded ${on ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-300'}`}>{w.label}</span>;
                    })}
                  </div>
                  {s.night_premium_type !== 'none' && (
                    <div className="text-xs text-purple-700 mt-1 flex items-center gap-1">
                      <Moon className="w-3 h-3" />
                      เบี้ยกะ {s.night_premium_type === 'percent' ? `${s.night_premium_value}%` : `${s.night_premium_value} บ.`}
                      {' '}({s.night_window_start}–{s.night_window_end})
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <button onClick={() => setEditing(s)} className="p-1 text-gray-400 hover:text-brand-600"><Pencil className="w-4 h-4" /></button>
                  {s.active && <button onClick={async () => { if (await confirm({ title: 'ปิดกะนี้?', danger: true, confirmText: 'ปิดกะ' })) await remove.mutateAsync(s.id); }} className="p-1 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
                </div>
              </div>
              <button onClick={() => setAssigning(s)} className="btn-secondary btn-slim mt-2 w-full justify-center">
                <Users className="w-3.5 h-3.5" /> เลือกพนักงาน / วันทำงาน
              </button>
            </div>
          ))}
        </div>
      )}

      {assigning && <AssignShiftModal shift={assigning} onClose={() => setAssigning(null)} />}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h2 className="font-semibold">{editing.id ? 'แก้ไข' : 'เพิ่ม'} กะ</h2>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label">Code *</label><input className="input font-mono" value={editing.code ?? ''} disabled={!!editing.id} onChange={(e) => setEditing({ ...editing, code: e.target.value })} /></div>
                <div><label className="label">ชื่อกะ *</label><input className="input" value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label">เข้างาน</label><input type="time" className="input" value={editing.start_time ?? ''} onChange={(e) => setEditing({ ...editing, start_time: e.target.value })} /></div>
                <div><label className="label">เลิกงาน</label><input type="time" className="input" value={editing.end_time ?? ''} onChange={(e) => setEditing({ ...editing, end_time: e.target.value })} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.crosses_midnight} onChange={(e) => setEditing({ ...editing, crosses_midnight: e.target.checked })} />
                กะข้ามเที่ยงคืน (เช่น 22:00→06:00)
              </label>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="label">พัก (นาที)</label><input type="number" className="input" value={editing.break_minutes ?? 60} onChange={(e) => setEditing({ ...editing, break_minutes: Number(e.target.value) })} /></div>
                <div><label className="label">ชม.เต็มวัน</label><input type="number" step="0.5" className="input" value={editing.paid_hours ?? 8} onChange={(e) => setEditing({ ...editing, paid_hours: Number(e.target.value) })} /></div>
              </div>

              <div>
                <label className="label">วันทำงานของกะ (จ–อา)</label>
                <div className="flex gap-1 flex-wrap">
                  {WEEKDAYS.map((w) => {
                    const days = editing.default_weekdays ?? [];
                    const on = days.includes(w.iso);
                    return (
                      <button
                        key={w.iso}
                        type="button"
                        onClick={() => setEditing({ ...editing, default_weekdays: on ? days.filter((d) => d !== w.iso) : [...days, w.iso].sort((a, b) => a - b) })}
                        className={`w-9 h-9 rounded-lg text-xs font-medium transition ${on ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-400'}`}
                      >{w.label}</button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-1">ใช้เป็นค่าเริ่มต้นตอนเลือกพนักงานเข้ากะ (ปรับรายคนได้)</p>
              </div>

              <div className="border-t pt-2">
                <div className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1"><Moon className="w-3.5 h-3.5" /> เบี้ยกะกลางคืน</div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="label">ประเภท</label>
                    <select className="input" value={editing.night_premium_type ?? 'none'} onChange={(e) => setEditing({ ...editing, night_premium_type: e.target.value as ShiftMasterRow['night_premium_type'] })}>
                      {NIGHT_PREMIUM_TYPES.map((t) => <option key={t} value={t}>{t === 'none' ? 'ไม่มี' : t === 'percent' ? '% ของ hourly' : 'เหมาจ่าย'}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">{editing.night_premium_type === 'percent' ? 'อัตรา %' : 'จำนวนบาท'}</label>
                    <input type="number" className="input" value={editing.night_premium_value ?? 0} disabled={editing.night_premium_type === 'none'} onChange={(e) => setEditing({ ...editing, night_premium_value: Number(e.target.value) })} />
                  </div>
                </div>
                {editing.night_premium_type !== 'none' && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><label className="label">ช่วงกลางคืนเริ่ม</label><input type="time" className="input" value={editing.night_window_start ?? '22:00'} onChange={(e) => setEditing({ ...editing, night_window_start: e.target.value })} /></div>
                    <div><label className="label">ถึง</label><input type="time" className="input" value={editing.night_window_end ?? '06:00'} onChange={(e) => setEditing({ ...editing, night_window_end: e.target.value })} /></div>
                  </div>
                )}
              </div>

              <div className="border-t pt-2">
                <div className="text-xs font-semibold text-teal-700 mb-2">⏱️ เวลายืดหยุ่น (flexible)</div>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5" checked={!!editing.flexible} onChange={(e) => setEditing({ ...editing, flexible: e.target.checked })} />
                  <span>กะยืดหยุ่น — ไม่คิดสาย (เข้าก่อนเวลาไหนก็ได้)</span>
                </label>
                {editing.flexible && (
                  <div className="mt-2 space-y-2 pl-6">
                    <label className="flex items-start gap-2 text-sm">
                      <input type="checkbox" className="mt-0.5" checked={editing.require_clock_out === false} onChange={(e) => setEditing({ ...editing, require_clock_out: !e.target.checked })} />
                      <span>สแกนเข้าอย่างเดียว — ไม่บังคับสแกนออก (ไม่คิดจากเวลาออก)</span>
                    </label>
                    <div>
                      <label className="label">ต้องสแกนเข้าก่อนเวลา (ไม่งั้นเตือน/ตาม)</label>
                      <input type="time" className="input" value={editing.flex_check_in_by ?? ''} onChange={(e) => setEditing({ ...editing, flex_check_in_by: e.target.value || null, start_time: e.target.value || editing.start_time })} />
                      <p className="text-[11px] text-gray-400 mt-1">ระบบจะตามให้สแกนหลังเลยเวลานี้ (ตั้งให้ตรงกับ “เข้างาน” ด้านบน)</p>
                    </div>
                  </div>
                )}
              </div>

              <button onClick={handleSave} disabled={upsert.isPending} className="btn-primary w-full">
                <Save className="w-4 h-4" /> {upsert.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// === Assign employees to a shift + pick their working weekdays ===
function AssignShiftModal({ shift, onClose }: { shift: ShiftMasterRow; onClose: () => void }) {
  const { data: employees } = useEmployees() as { data: EmpLite[] | undefined };
  const { data: schedule } = useWorkSchedule();
  const { data: assigns } = useShiftAssignmentsByShift(shift.id);
  const upsert = useUpsertShiftAssignment();
  const remove = useDeleteShiftAssignment();
  const toast = useToast();

  const weekend = new Set<number>(schedule?.weekend_days ?? [7]);
  // Prefer the shift's declared working days; fall back to all non-weekend days.
  const defaultWeekdays = (shift.default_weekdays && shift.default_weekdays.length > 0)
    ? shift.default_weekdays
    : WEEKDAYS.map((w) => w.iso).filter((d) => !weekend.has(d));

  const byEmp = new Map<string, ShiftAssignmentRow>();
  for (const a of (assigns ?? []) as ShiftAssignmentRow[]) if (a.active) byEmp.set(a.emp_code, a);

  async function toggleEmp(emp_code: string, on: boolean) {
    const existing = byEmp.get(emp_code);
    if (on && !existing) {
      await upsert.mutateAsync({ emp_code, shift_id: shift.id, weekdays: defaultWeekdays, start_date: firstOfMonthIso(), active: true });
    } else if (!on && existing) {
      await remove.mutateAsync(existing.id);
    }
  }
  async function toggleWeekday(row: ShiftAssignmentRow, iso: number) {
    const set = new Set(row.weekdays ?? []);
    if (set.has(iso)) set.delete(iso); else set.add(iso);
    if (set.size === 0) { toast.error('ต้องมีวันทำงานอย่างน้อย 1 วัน'); return; }
    await upsert.mutateAsync({ id: row.id, emp_code: row.emp_code, shift_id: shift.id, weekdays: [...set].sort((a, b) => a - b), start_date: row.start_date, active: true });
  }

  const list = (employees ?? []).filter((e) => e.emp_code);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end lg:items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-2xl p-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-1">
          <h2 className="font-semibold">เลือกพนักงานเข้ากะ</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-500 mb-3">{shift.name} · {shift.start_time}–{shift.end_time}</p>
        <EmployeeMultiPicker
          employees={list}
          isChecked={(e) => byEmp.has(e.emp_code)}
          onToggle={(e, on) => toggleEmp(e.emp_code, on)}
          maxHeightClass="max-h-[55vh]"
          renderExpanded={(e) => {
            const row = byEmp.get(e.emp_code);
            if (!row) return null;
            return (
              <div>
                <div className="text-[11px] text-gray-500 mb-1">วันทำงาน (ที่ไม่เลือก = วันหยุด)</div>
                <div className="flex flex-wrap gap-1">
                  {WEEKDAYS.map((w) => {
                    const active = (row.weekdays ?? []).includes(w.iso);
                    return (
                      <button key={w.iso} onClick={() => toggleWeekday(row, w.iso)}
                        className={`w-8 h-8 rounded-full text-xs font-medium transition ${active ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                        {w.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          }}
        />
        <p className="text-[11px] text-gray-400 mt-3">ปุ่มวันสีฟ้า = วันทำงาน · สีเทา = วันหยุด · ติ๊กออก = นำออกจากกะนี้</p>
      </div>
    </div>
  );
}

function SwapTab() {
  const pending = useSwapRequests('pending');
  const decide = useDecideSwap();
  const toast = useToast();

  return (
    <div className="space-y-2">
      {pending.isLoading ? (
        <p className="text-center text-gray-500 py-8">กำลังโหลด…</p>
      ) : (pending.data ?? []).length === 0 ? (
        <p className="text-center text-gray-500 py-8">ไม่มีคำขอแลกเวรค้างอยู่</p>
      ) : (
        (pending.data ?? []).map((s) => (
          <div key={s.id} className="card p-3">
            <div className="text-sm font-medium">{s.date}</div>
            <div className="text-xs text-gray-600 mt-1">
              <span className="font-mono">{s.requester_emp_code}</span> ⇄ <span className="font-mono">{s.target_emp_code}</span>
            </div>
            {s.reason && <div className="text-xs text-gray-500 mt-1">เหตุผล: {s.reason}</div>}
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => decide.mutateAsync({ id: s.id, decision: 'approved' }).catch((e) => toast.error(e.message))}
                className="btn-primary text-xs flex-1"
              >
                <Check className="w-3.5 h-3.5" /> อนุมัติ
              </button>
              <button
                onClick={() => decide.mutateAsync({ id: s.id, decision: 'rejected' }).catch((e) => toast.error(e.message))}
                className="btn-secondary text-xs flex-1 text-red-700 border-red-200"
              >
                <XCircle className="w-3.5 h-3.5" /> ปฏิเสธ
              </button>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ===== แพทเทิร์นหมุนเวรอัตโนมัติ =====
const OFF_CELL = '__OFF__';
function thisMonthStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function RotationTab() {
  const patterns = useRotationPatterns();
  const shifts = useShiftMaster();
  const upsert = useUpsertRotationPattern();
  const del = useDeleteRotationPattern();
  const generate = useGenerateRotation();
  const toast = useToast();
  const confirm = useConfirm();

  const [editing, setEditing] = useState<RotationPatternRow | 'new' | null>(null);
  const [period, setPeriod] = useState(thisMonthStr());
  const shiftOpts = (shifts.data ?? []).filter((s) => s.active);
  const shiftName = (id: string | null) => id === null ? 'หยุด' : (shiftOpts.find((s) => s.id === id)?.name ?? '?');

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        ตั้งรอบหมุนครั้งเดียว (เช่น เช้า-เช้า-บ่าย-บ่าย-ดึก-ดึก-หยุด) แล้วกด “สร้างตารางเดือนนี้”
        ระบบจะลงปฏิทินให้พนักงานที่ผูกแพทเทิร์นไว้ (ผูกได้ในหน้าพนักงาน). วันที่หัวหน้าแก้เองจะไม่ถูกทับ.
      </p>

      {/* generate */}
      <div className="card p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="label">เดือน</label>
          <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="input" />
        </div>
        <button
          onClick={() => generate.mutate(period, {
            onSuccess: (r) => toast.success(`สร้างตารางหมุนแล้ว ${r.written} ช่อง (${r.employees} คน${r.skipped_manual ? ` · เว้นที่แก้มือ ${r.skipped_manual}` : ''})`),
            onError: (e) => toast.error(e instanceof Error ? e.message : 'สร้างไม่สำเร็จ'),
          })}
          disabled={generate.isPending}
          className="btn-cta inline-flex items-center gap-1.5"
        >
          <RotateCw className="w-4 h-4" /> {generate.isPending ? 'กำลังสร้าง…' : 'สร้างตารางเดือนนี้'}
        </button>
      </div>

      {/* list */}
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-700">รอบหมุนที่มี</div>
        <button onClick={() => setEditing('new')} className="btn-secondary inline-flex items-center gap-1 text-sm">
          <Plus className="w-4 h-4" /> เพิ่มรอบหมุน
        </button>
      </div>
      {(patterns.data ?? []).length === 0 ? (
        <p className="text-sm text-gray-400">ยังไม่มีรอบหมุน</p>
      ) : (
        <div className="space-y-2">
          {(patterns.data ?? []).map((p) => (
            <div key={p.id} className="card p-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-gray-900">{p.name} <span className="text-xs text-gray-400">({p.code})</span></div>
                <div className="text-xs text-gray-500 mt-0.5">รอบ {p.cycle_length} วัน: {p.sequence.map(shiftName).join(' · ')}</div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setEditing(p)} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4 text-gray-500" /></button>
                <button
                  onClick={async () => {
                    if (await confirm({ title: 'ลบรอบหมุน?', message: 'พนักงานที่ผูกไว้จะถูกปลดออก (ตารางที่สร้างแล้วยังอยู่)', confirmText: 'ลบ', danger: true })) {
                      del.mutate(p.id, { onSuccess: () => toast.success('ลบแล้ว'), onError: (e) => toast.error(e instanceof Error ? e.message : 'ลบไม่สำเร็จ') });
                    }
                  }}
                  className="p-1.5 rounded hover:bg-red-50"
                ><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <RotationEditor
          initial={editing === 'new' ? null : editing}
          shiftOpts={shiftOpts}
          onClose={() => setEditing(null)}
          onSave={(body) => upsert.mutate(body, {
            onSuccess: () => { toast.success('บันทึกรอบหมุนแล้ว'); setEditing(null); },
            onError: (e) => toast.error(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ'),
          })}
          saving={upsert.isPending}
        />
      )}
    </div>
  );
}

function RotationEditor({ initial, shiftOpts, onClose, onSave, saving }: {
  initial: RotationPatternRow | null;
  shiftOpts: ShiftMasterRow[];
  onClose: () => void;
  onSave: (body: { id?: string; code: string; name: string; sequence: (string | null)[]; active: boolean }) => void;
  saving: boolean;
}) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [seq, setSeq] = useState<(string | null)[]>(initial?.sequence ?? [shiftOpts[0]?.id ?? null]);

  const cellValue = (v: string | null) => v === null ? OFF_CELL : v;
  const setCell = (i: number, raw: string) => setSeq((s) => s.map((v, idx) => idx === i ? (raw === OFF_CELL ? null : raw) : v));

  return (
    <div className="fixed inset-0 z-40 bg-black/30 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{initial ? 'แก้รอบหมุน' : 'เพิ่มรอบหมุน'}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">รหัส</label><input value={code} onChange={(e) => setCode(e.target.value)} placeholder="rot6on1" className="input" /></div>
          <div><label className="label">ชื่อ</label><input value={name} onChange={(e) => setName(e.target.value)} placeholder="ทำ 6 หยุด 1" className="input" /></div>
        </div>
        <div>
          <label className="label">ลำดับกะใน 1 รอบ ({seq.length} วัน)</label>
          <div className="space-y-1.5 mt-1">
            {seq.map((v, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs text-gray-400 w-10">วันที่ {i + 1}</span>
                <select value={cellValue(v)} onChange={(e) => setCell(i, e.target.value)} className="input flex-1 py-1">
                  {shiftOpts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  <option value={OFF_CELL}>หยุด</option>
                </select>
                <button onClick={() => setSeq((s) => s.filter((_, idx) => idx !== i))} disabled={seq.length <= 1} className="p-1 text-red-400 disabled:opacity-30"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setSeq((s) => [...s, shiftOpts[0]?.id ?? null])} className="btn-secondary text-xs mt-2 inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> เพิ่มวัน</button>
        </div>
        <button
          onClick={() => {
            if (!code.trim() || !name.trim()) return;
            onSave({ id: initial?.id, code: code.trim(), name: name.trim(), sequence: seq, active: true });
          }}
          disabled={saving || !code.trim() || !name.trim()}
          className="btn-cta w-full justify-center flex items-center gap-2"
        ><Save className="w-4 h-4" /> {saving ? 'กำลังบันทึก…' : 'บันทึก'}</button>
      </div>
    </div>
  );
}
